import React, { useCallback } from 'react';
import { Switch, Text, notify } from '@shootsta/common-react';
import type { VideoShareSettings, UpdateInput } from '../hooks/useVideoShareSettings';

type ShareStatusToggleProps = {
  settings: VideoShareSettings | null;
  updateSettings: (input: UpdateInput) => Promise<any>;
  disabled?: boolean;
};

function ShareStatusToggle({
  settings,
  updateSettings,
  disabled = false
}: ShareStatusToggleProps) {
  const isPublic = settings?.shareStatus === 'PUBLIC';

  const handleToggle = useCallback(async () => {
    try {
      if (isPublic) {
        await updateSettings({ shareStatus: 'PRIVATE' });
        notify({ content: 'Video set to private', type: 'success' });
      } else {
        await updateSettings({ shareStatus: 'PUBLIC', accessLevel: 'GUEST' });
        notify({ content: 'Video set to public', type: 'success' });
      }
    } catch {
      notify({ content: 'Failed to update share status', type: 'error' });
    }
  }, [isPublic, updateSettings]);

  return (
    <div className="video-share-modal__status-toggle">
      <div
        className={`video-share-modal__status-badge video-share-modal__status-badge--${isPublic ? 'public' : 'private'}`}
      >
        <div className="video-share-modal__status-badge__content">
          <Text noMargin highlighted={isPublic}>
            {isPublic
              ? 'This video is publicly shared'
              : 'This video is not currently being shared'}
          </Text>
          <Text noMargin placeholder className="video-share-modal__status-badge__hint">
            {isPublic
              ? 'Anyone with the public link can access this video'
              : 'Enable public sharing to generate a shareable link'}
          </Text>
        </div>
      </div>
      <div className="video-share-modal__status-toggle__control">
        <Text noMargin highlighted>
          Public Sharing
        </Text>
        <Switch
          id="video-share-public-toggle"
          onChange={handleToggle}
          checked={isPublic}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export default ShareStatusToggle;
